import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, MessageCircle } from "lucide-react";
import { sendBalanceReminder } from "@/lib/whatsapp";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthLabel = (p) => { const [y, m] = (p || "").split("-"); return AYLAR[+m - 1] ? `${AYLAR[+m - 1]} ${y}` : p; };
const shortMonth = (p) => { const [y, m] = (p || "").split("-"); return AYLAR[+m - 1] ? `${AYLAR[+m - 1].slice(0, 3)} ${String(y).slice(2)}` : p; };
const fmtD = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "-");
const pct = (v) => (v === null || v === undefined ? "-" : `%${String(v).replace(".", ",")}`);

async function downloadXlsx(url, params, fname) {
  try {
    const res = await api.get(url, { params, responseType: "blob" });
    const href = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = href; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(href);
  } catch { toast.error("Excel oluşturulamadı"); }
}

async function openPdf(url, params) {
  const w = window.open("", "_blank");
  try {
    const res = await api.get(url, { params, responseType: "blob" });
    const href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (w) w.location.href = href; else window.open(href, "_blank");
  } catch { if (w) w.close(); toast.error("PDF oluşturulamadı"); }
}

// Her raporun sağ üstündeki Excel / PDF butonları: base = "/reports/<ad>"
function ExportBar({ base, params, fname }) {
  return (
    <div className="flex gap-2">
      <Button variant="outline" data-testid={`xlsx-${base.split("/").pop()}`} onClick={() => downloadXlsx(`${base}/xlsx`, params, fname)}>
        <FileSpreadsheet size={15} className="mr-1.5" /> Excel
      </Button>
      <Button variant="outline" data-testid={`pdf-${base.split("/").pop()}`} onClick={() => openPdf(`${base}/pdf`, params)}>
        <FileText size={15} className="mr-1.5" /> PDF
      </Button>
    </div>
  );
}

function Kpi({ label, value, sub, cls = "" }) {
  return (
    <Card className="p-5">
      <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className={`font-head text-2xl font-semibold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function MethodBars({ methods }) {
  const total = methods.reduce((a, m) => a + m.tutar, 0);
  if (!methods.length) return <p className="text-sm text-muted-foreground">Bu dönemde tahsilat kaydı yok.</p>;
  return (
    <div className="space-y-2.5">
      {methods.map((m) => (
        <div key={m.yontem}>
          <div className="flex justify-between text-sm mb-1"><span className="font-medium">{m.yontem} <span className="text-muted-foreground font-normal">· {m.adet} işlem</span></span><span>{fmtTL(m.tutar)} <span className="text-muted-foreground">({total ? Math.round((m.tutar / total) * 100) : 0}%)</span></span></div>
          <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${total ? (m.tutar / total) * 100 : 0}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function General() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Aktif Mükellef" value={d.total_clients} />
        <Kpi label="Beyanname Tamamlama" value={`%${d.declaration_completion}`} cls="text-emerald-600" />
        <Kpi label="Personel Sayısı" value={d.workload.length} />
      </div>
      <Card className="p-5">
        <h3 className="font-head font-medium mb-4">Aylık Tahakkuk & Tahsilat</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={d.monthly}>
            <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
            <Tooltip formatter={(v) => fmtTL(v)} />
            <Legend />
            <Bar dataKey="tahakkuk" name="Tahakkuk" fill="#1e3a5f" radius={[6, 6, 0, 0]} />
            <Bar dataKey="tahsilat" name="Tahsilat" fill="#0d9488" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function Receivables() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [only60, setOnly60] = useState(false);
  useEffect(() => { api.get("/reports/receivables", { params: { months: 12 } }).then((r) => setD(r.data)).catch(() => toast.error("Rapor yüklenemedi")); }, []);
  const rows = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    return d.clients.filter((r) => (!s || (r.unvan || "").toLowerCase().includes(s)) && (!only60 || r.b61_90 + r.b90_plus > 0));
  }, [d, q, only60]);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;
  const t = d.totals;
  const aging = [
    { name: "0-30 gün", v: t.b0_30, color: "#0d9488" }, { name: "31-60 gün", v: t.b31_60, color: "#d97706" },
    { name: "61-90 gün", v: t.b61_90, color: "#ea580c" }, { name: "90+ gün", v: t.b90_plus, color: "#e11d48" },
  ];
  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <ExportBar base="/reports/receivables" params={{ months: 12 }} fname={`Alacak_Tahsilat_Raporu_${d.generated}.xlsx`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Toplam Alacak" value={fmtTL(t.toplam)} sub={`${t.adet} mükellefte`} cls="text-rose-600" />
        <Kpi label="60 Günü Geçen" value={fmtTL(t.b61_90 + t.b90_plus)} sub="61 gün ve üzeri" cls={t.b61_90 + t.b90_plus > 0 ? "text-orange-600" : ""} />
        <Kpi label="Tahsilat Oranı (12 ay)" value={pct(d.collection_rate)} sub="tahsilat / tahakkuk" cls="text-emerald-600" />
        <Kpi label="En Yüksek Alacak" value={d.clients[0] ? fmtTL(d.clients[0].bakiye) : "-"} sub={d.clients[0]?.unvan} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-head font-medium mb-3">Alacak Yaşlandırma</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={aging}>
              <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtTL(v)} />
              <Bar dataKey="v" name="Alacak" radius={[6, 6, 0, 0]}>{aging.map((a) => <Cell key={a.name} fill={a.color} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-head font-medium mb-3">Tahsilatın Ödeme Türüne Dağılımı <span className="text-xs text-muted-foreground font-normal">(son 12 ay)</span></h3>
          <MethodBars methods={d.methods} />
          <p className="text-[11px] text-muted-foreground mt-3">Ödeme türü seçeneği yeni eklendi; öncesindeki kayıtlar otomatik "Banka" sayılmıştır.</p>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-head font-medium mb-4">Aylık Tahakkuk, Tahsilat ve Oran</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={d.monthly.map((m) => ({ ...m, label: shortMonth(m.month) }))}>
            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v) => fmtTL(v)} />
            <Legend />
            <Bar dataKey="tahakkuk" name="Tahakkuk" fill="#1e3a5f" radius={[6, 6, 0, 0]} />
            <Bar dataKey="tahsilat" name="Tahsilat" fill="#0d9488" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="overflow-x-auto mt-3">
          <table className="text-xs w-full">
            <thead><tr className="text-muted-foreground"><th className="text-left py-1 font-medium">Ay</th>{d.monthly.map((m) => <th key={m.month} className="text-right py-1 font-medium px-2">{shortMonth(m.month)}</th>)}</tr></thead>
            <tbody><tr><td className="py-1 text-muted-foreground">Oran</td>{d.monthly.map((m) => <td key={m.month} className={`text-right px-2 ${m.oran !== null && m.oran < 70 ? "text-rose-600" : "text-emerald-600"}`}>{pct(m.oran)}</td>)}</tr></tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-border">
          <h3 className="font-head font-medium mr-auto">Borçlu Mükellefler</h3>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={only60} onChange={(e) => setOnly60(e.target.checked)} /> Sadece 60 günü geçenler</label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mükellef ara..." className="w-52" data-testid="receivables-search" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr>
              <th className="text-left px-4 py-2.5 font-medium">Mükellef</th>
              <th className="text-right px-3 py-2.5 font-medium">Toplam</th>
              <th className="text-right px-3 py-2.5 font-medium">0-30</th>
              <th className="text-right px-3 py-2.5 font-medium">31-60</th>
              <th className="text-right px-3 py-2.5 font-medium">61-90</th>
              <th className="text-right px-3 py-2.5 font-medium">90+</th>
              <th className="text-left px-3 py-2.5 font-medium">Son Ödeme</th>
              <th className="px-3 py-2.5 w-28"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client_id} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5"><button className="font-medium hover:underline text-left" onClick={() => nav(`/mukellefler/${r.client_id}`)}>{r.unvan}</button>{!r.aktif && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Ayrıldı</span>}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-rose-600">{fmtTL(r.bakiye)}</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">{r.b0_30 ? fmtTL(r.b0_30) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">{r.b31_60 ? fmtTL(r.b31_60) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-orange-600">{r.b61_90 ? fmtTL(r.b61_90) : "—"}</td>
                  <td className="px-3 py-2.5 text-right text-rose-600">{r.b90_plus ? fmtTL(r.b90_plus) : "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtD(r.son_odeme)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Button size="sm" variant="outline" onClick={() => sendBalanceReminder(r.client_id)} className="text-green-700 border-green-300 hover:bg-green-50" title="Bakiye hatırlatması + cari ekstre (WhatsApp)"><MessageCircle size={13} className="mr-1" /> Hatırlat</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Gösterilecek borçlu mükellef yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function MonthSummary() {
  const nav = useNavigate();
  const now = new Date();
  const opts = useMemo(() => {
    const out = []; const d = new Date(now.getFullYear(), now.getMonth(), 1);
    for (let i = 0; i < 18; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [period, setPeriod] = useState(opts[0]);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/reports/month-summary", { params: { period } }).then((r) => setD(r.data)).catch(() => toast.error("Rapor yüklenemedi")); }, [period]);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <select data-testid="month-select" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          {opts.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
        </select>
        <ExportBar base="/reports/month-summary" params={{ period }} fname={`Ay_Sonu_Ozeti_${period}.xlsx`} />
      </div>
      {!d ? <div className="text-muted-foreground">Yükleniyor...</div> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Tahakkuk" value={fmtTL(d.tahakkuk)} sub={`${monthLabel(d.period)} içinde işlenen borç`} />
          <Kpi label="Tahsilat" value={fmtTL(d.tahsilat)} sub={`${d.odeme_yapan} mükellef ödeme yaptı`} cls="text-emerald-600" />
          <Kpi label="Tahsilat Oranı" value={pct(d.oran)} sub={d.net >= 0 ? `Tahakkukun ${fmtTL(d.net)} üzerinde tahsil edildi` : `Tahakkukun ${fmtTL(-d.net)} altında`} cls={d.oran !== null && d.oran < 70 ? "text-rose-600" : "text-emerald-600"} />
          <Kpi label="Ödeme Yapmayan" value={d.odemeyen} sub={`ay sonu bakiyesi ${fmtTL(d.odemeyen_toplam)}`} cls={d.odemeyen > 0 ? "text-orange-600" : ""} />
        </div>
        <Card className="p-5"><h3 className="font-head font-medium mb-3">Tahsilatın Ödeme Türü</h3><MethodBars methods={d.methods} /></Card>

        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border"><h3 className="font-head font-medium">Bu Ay Ödeme Yapmayan Mükellefler</h3><p className="text-xs text-muted-foreground mt-0.5">Ay sonunda borcu olan ve bu ay hiç tahsilat girilmemiş aktif mükellefler</p></div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr>
              <th className="text-left px-4 py-2.5 font-medium">Mükellef</th><th className="text-right px-3 py-2.5 font-medium">Ay İçi Tahakkuk</th>
              <th className="text-right px-3 py-2.5 font-medium">Ay Sonu Bakiye</th><th className="text-left px-3 py-2.5 font-medium">Son Ödeme</th><th className="w-28"></th></tr></thead>
            <tbody>
              {d.nonpayers.map((r) => (
                <tr key={r.client_id} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5"><button className="font-medium hover:underline text-left" onClick={() => nav(`/mukellefler/${r.client_id}`)}>{r.unvan}</button></td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">{r.ay_tahakkuk ? fmtTL(r.ay_tahakkuk) : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-rose-600">{fmtTL(r.bakiye)}</td>
                  <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtD(r.son_odeme)}</td>
                  <td className="px-3 py-2.5 text-right"><Button size="sm" variant="outline" onClick={() => sendBalanceReminder(r.client_id)} className="text-green-700 border-green-300 hover:bg-green-50"><MessageCircle size={13} className="mr-1" /> Hatırlat</Button></td>
                </tr>
              ))}
              {d.nonpayers.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Herkes ödeme yapmış veya borcu yok. 🎉</td></tr>}
            </tbody></table></div>
        </Card>

        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border"><h3 className="font-head font-medium">Ayın Tahsilatları</h3></div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr>
              <th className="text-left px-4 py-2.5 font-medium">Tarih</th><th className="text-left px-3 py-2.5 font-medium">Mükellef</th>
              <th className="text-right px-3 py-2.5 font-medium">Tutar</th><th className="text-left px-3 py-2.5 font-medium">Ödeme Türü</th><th className="text-left px-3 py-2.5 font-medium">Makbuz</th></tr></thead>
            <tbody>
              {d.payments.map((p, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmtD(p.date)}</td>
                  <td className="px-3 py-2.5"><button className="hover:underline text-left" onClick={() => nav(`/mukellefler/${p.client_id}`)}>{p.unvan}</button></td>
                  <td className="px-3 py-2.5 text-right font-medium text-emerald-600">{fmtTL(p.tutar)}</td>
                  <td className="px-3 py-2.5">{p.yontem}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{p.makbuz_no || "—"}</td>
                </tr>
              ))}
              {d.payments.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Bu ay tahsilat kaydı yok.</td></tr>}
            </tbody></table></div>
        </Card>
      </>)}
    </div>
  );
}

// geçmişe doğru ay seçenekleri (en yeni başta); defaultPrev: varsayılan olarak geçen ay
function usePeriods(n = 14) {
  return useMemo(() => {
    const out = []; const d = new Date(); d.setDate(1);
    for (let i = 0; i < n; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); }
    return out;
  }, [n]);
}

const th = "px-3 py-2.5 font-medium";
const thead = "bg-secondary/60 text-xs uppercase text-muted-foreground";

function PeriodSelect({ value, onChange, opts, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
      {opts.map((p) => <option key={p} value={p}>{monthLabel(p)}</option>)}
    </select>
  );
}

function DeclarationReport() {
  const nav = useNavigate();
  const opts = usePeriods();
  const [period, setPeriod] = useState(opts[1]); // geçen ay
  const [d, setD] = useState(null);
  const [onlyLate, setOnlyLate] = useState(false);
  useEffect(() => { setD(null); api.get("/reports/declarations", { params: { period } }).then((r) => setD(r.data)).catch(() => toast.error("Rapor yüklenemedi")); }, [period]);
  const t = d?.totals;
  const pending = useMemo(() => (d ? d.pending.filter((p) => !onlyLate || (p.days !== null && p.days < 0)) : []), [d, onlyLate]);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodSelect value={period} onChange={setPeriod} opts={opts} testid="decl-rep-period" />
        <ExportBar base="/reports/declarations" params={{ period }} fname={`Beyanname_Performansi_${period}.xlsx`} />
      </div>
      {!d ? <div className="text-muted-foreground">Yükleniyor...</div> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <Kpi label="Toplam" value={t.toplam} />
          <Kpi label="Tamamlanan" value={t.tamam} cls="text-emerald-600" />
          <Kpi label="Bekleyen" value={t.bekleyen} cls={t.bekleyen ? "text-amber-600" : ""} />
          <Kpi label="Geciken" value={t.geciken} cls={t.geciken ? "text-rose-600" : ""} />
          <Kpi label="Muaf" value={t.muaf} />
          <Kpi label="Tamamlama" value={pct(t.oran)} cls="text-emerald-600" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="font-head font-medium mb-3">Beyanname Türüne Göre</h3>
            <div className="space-y-3">
              {d.types.map((x) => (
                <div key={x.type}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium">{x.type} <span className="text-muted-foreground font-normal">· {x.tamam}/{x.toplam - x.muaf}{x.muaf ? ` (+${x.muaf} muaf)` : ""}</span></span><span className={x.geciken ? "text-rose-600" : "text-muted-foreground"}>{x.geciken ? `${x.geciken} geciken · ` : ""}{pct(x.oran)}</span></div>
                  <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-emerald-600" style={{ width: `${x.oran || 0}%` }} /></div>
                </div>
              ))}
              {d.types.length === 0 && <p className="text-sm text-muted-foreground">Bu dönemde beyanname yok.</p>}
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="font-head font-medium mb-3">Son 6 Dönem Tamamlama Oranı</h3>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={d.trend.map((x) => ({ ...x, label: shortMonth(x.period), oran: x.oran || 0 }))}>
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `%${v}`} />
                <Tooltip formatter={(v) => `%${v}`} />
                <Bar dataKey="oran" name="Tamamlama" fill="#0d9488" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
        <Card className="overflow-hidden">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-border">
            <h3 className="font-head font-medium mr-auto">Bekleyen ve Geciken Beyannameler ({pending.length})</h3>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyLate} onChange={(e) => setOnlyLate(e.target.checked)} /> Sadece gecikenler</label>
          </div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className={thead}><tr><th className="text-left px-4 py-2.5 font-medium">Mükellef</th><th className={`text-left ${th}`}>Tür</th><th className={`text-left ${th}`}>Durum</th><th className={`text-left ${th}`}>Son Tarih</th><th className={`text-left ${th}`}>Kalan</th><th className={`text-left ${th}`}>Sorumlu</th></tr></thead>
            <tbody>
              {pending.map((p, i) => (
                <tr key={i} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5"><button className="font-medium hover:underline text-left" onClick={() => nav(`/mukellefler/${p.client_id}`)}>{p.unvan}</button></td>
                  <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded bg-secondary">{p.type}</span></td>
                  <td className="px-3 py-2.5 text-muted-foreground">{p.status}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{fmtD(p.deadline)}</td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${p.days !== null && p.days < 0 ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>{p.days === null ? "-" : p.days < 0 ? `${-p.days} gün gecikti` : `${p.days} gün`}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{p.sorumlu}</td>
                </tr>
              ))}
              {pending.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Bekleyen beyanname yok. 🎉</td></tr>}
            </tbody></table></div>
        </Card>
      </>)}
    </div>
  );
}

function PortfolioReport() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/portfolio").then((r) => setD(r.data)).catch(() => toast.error("Rapor yüklenemedi")); }, []);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;
  return (
    <div className="space-y-5">
      <div className="flex justify-end"><ExportBar base="/reports/portfolio" params={{}} fname={`Gelir_Portfoy_${d.generated}.xlsx`} /></div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Kpi label="Aylık Sözleşme Geliri" value={fmtTL(d.aylik_gelir)} sub={`${d.aktif} aktif mükellef`} cls="text-emerald-600" />
        <Kpi label="Yıllık Projeksiyon" value={fmtTL(d.yillik_gelir)} sub="aylık gelir × 12" />
        <Kpi label="Mükellef Başı Ortalama" value={fmtTL(d.ortalama)} />
        <Kpi label="Ücreti 0 Görünen" value={d.ucretsiz} sub="aylık ücret girilmemiş mükellef" cls={d.ucretsiz ? "text-orange-600" : ""} />
        <Kpi label="e-Defter / e-Fatura" value={`${d.edefter} / ${d.efatura}`} sub="mükellef sayısı" />
        <Kpi label="Son 12 Ay Ayrılan" value={d.left.length} sub={d.left.length ? `${fmtTL(d.left_loss)}/ay gelir kaybı` : "ayrılan yok"} cls={d.left.length ? "text-rose-600" : ""} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-head font-medium mb-3">Şirket Türüne Göre</h3>
          <div className="space-y-3">
            {d.by_type.map((x) => (
              <div key={x.tur}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium">{x.tur} <span className="text-muted-foreground font-normal">· {x.adet} mükellef</span></span><span>{fmtTL(x.gelir)} <span className="text-muted-foreground">({pct(x.pay)})</span></span></div>
                <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-sky-700" style={{ width: `${x.pay || 0}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-head font-medium mb-3">Beyanname Türüne Göre Mükellef</h3>
          <ResponsiveContainer width="100%" height={Math.max(160, d.by_beyan.length * 34)}>
            <BarChart data={d.by_beyan} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="tur" fontSize={11} tickLine={false} axisLine={false} width={90} />
              <Tooltip />
              <Bar dataKey="adet" name="Mükellef" fill="#1e3a5f" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border"><h3 className="font-head font-medium">Aylık Ücreti 0 Görünen Mükellefler ({d.zero.length})</h3><p className="text-xs text-muted-foreground mt-0.5">Mükellef bilgilerinde aylık ücret girilmemiş; ücret girilirse sözleşme gelirine ve aylık tahakkuka dahil olur.</p></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className={thead}><tr><th className="text-left px-4 py-2.5 font-medium">Mükellef</th><th className={`text-left ${th}`}>Şirket Türü</th><th className={`text-right ${th}`}>Beyanname</th><th className={`text-left ${th}`}>Sorumlu</th></tr></thead>
          <tbody>
            {d.zero.map((x) => (
              <tr key={x.client_id} className="border-t border-border hover:bg-accent/40">
                <td className="px-4 py-2.5"><button className="font-medium hover:underline text-left" onClick={() => nav(`/mukellefler/${x.client_id}`)}>{x.unvan}</button></td>
                <td className="px-3 py-2.5 text-muted-foreground">{x.sirket_turu}</td>
                <td className="px-3 py-2.5 text-right">{x.beyanname}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{x.sorumlu}</td>
              </tr>
            ))}
            {d.zero.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Tüm mükelleflerin ücreti girilmiş. 🎉</td></tr>}
          </tbody></table></div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border"><h3 className="font-head font-medium">En Yüksek Ücretli 10 Mükellef</h3></div>
          <table className="w-full text-sm"><tbody>
            {d.top.map((x) => (
              <tr key={x.client_id} className="border-t border-border first:border-0"><td className="px-4 py-2.5"><button className="hover:underline text-left" onClick={() => nav(`/mukellefler/${x.client_id}`)}>{x.unvan}</button></td><td className="px-4 py-2.5 text-right font-medium">{fmtTL(x.ucret)}</td></tr>
            ))}
          </tbody></table>
        </Card>
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border"><h3 className="font-head font-medium">Son 12 Ayda Ayrılanlar</h3></div>
          <table className="w-full text-sm"><tbody>
            {d.left.map((x, i) => (
              <tr key={i} className="border-t border-border first:border-0"><td className="px-4 py-2.5">{x.unvan}<div className="text-xs text-muted-foreground">{fmtD(x.tarih)} · {x.neden}</div></td><td className="px-4 py-2.5 text-right text-rose-600 font-medium">{fmtTL(x.ucret)}/ay</td></tr>
            ))}
            {d.left.length === 0 && <tr><td className="text-center py-8 text-muted-foreground">Ayrılan mükellef yok.</td></tr>}
          </tbody></table>
        </Card>
      </div>
    </div>
  );
}

function WorkloadReport() {
  const opts = usePeriods();
  const [period, setPeriod] = useState(opts[1]);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/reports/workload", { params: { period } }).then((r) => setD(r.data)).catch(() => toast.error("Rapor yüklenemedi")); }, [period]);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Beyanname dönemi:</span><PeriodSelect value={period} onChange={setPeriod} opts={opts} testid="wl-period" /></div>
        <ExportBar base="/reports/workload" params={{ period }} fname={`Personel_Is_Yuku_${period}.xlsx`} />
      </div>
      {!d ? <div className="text-muted-foreground">Yükleniyor...</div> : (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Toplam Mükellef" value={d.totals.mukellef} sub={`${d.rows.length} personel`} />
          <Kpi label="Beyanname (muaf hariç)" value={d.totals.beyanname} sub={`${d.totals.tamam} tamamlandı`} />
          <Kpi label="Geciken Beyanname" value={d.totals.geciken} cls={d.totals.geciken ? "text-rose-600" : ""} />
          <Kpi label="Açık Görev" value={d.totals.gorev} sub={`${d.totals.gorev_geciken} tanesi gecikmiş`} cls={d.totals.gorev_geciken ? "text-orange-600" : ""} />
        </div>
        <Card className="p-5">
          <h3 className="font-head font-medium mb-3">Personel Bazında Beyanname Durumu</h3>
          <ResponsiveContainer width="100%" height={Math.max(180, d.rows.length * 46)}>
            <BarChart data={d.rows.map((r) => ({ ...r, ok: r.tamam, bek: r.bekleyen - r.geciken, gec: r.geciken }))} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="personel" fontSize={11} tickLine={false} axisLine={false} width={110} />
              <Tooltip />
              <Legend />
              <Bar dataKey="ok" name="Tamamlanan" stackId="a" fill="#0d9488" />
              <Bar dataKey="bek" name="Bekleyen" stackId="a" fill="#d97706" />
              <Bar dataKey="gec" name="Geciken" stackId="a" fill="#e11d48" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className={thead}><tr>
              <th className="text-left px-4 py-2.5 font-medium">Personel</th><th className={`text-right ${th}`}>Mükellef</th><th className={`text-right ${th}`}>Aylık Gelir</th>
              <th className={`text-right ${th}`}>Beyanname</th><th className={`text-right ${th}`}>Tamam</th><th className={`text-right ${th}`}>Bekleyen</th><th className={`text-right ${th}`}>Geciken</th>
              <th className={`text-right ${th}`}>Tamamlama</th><th className={`text-right ${th}`}>Açık Görev</th><th className={`text-right ${th}`}>Geciken Görev</th></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.personel} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5 font-medium">{r.personel}</td>
                  <td className="px-3 py-2.5 text-right">{r.mukellef}</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">{fmtTL(r.gelir)}</td>
                  <td className="px-3 py-2.5 text-right">{r.beyanname}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-600">{r.tamam}</td>
                  <td className="px-3 py-2.5 text-right text-amber-600">{r.bekleyen}</td>
                  <td className={`px-3 py-2.5 text-right ${r.geciken ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{r.geciken}</td>
                  <td className="px-3 py-2.5 text-right">{pct(r.oran)}</td>
                  <td className="px-3 py-2.5 text-right">{r.gorev}</td>
                  <td className={`px-3 py-2.5 text-right ${r.gorev_geciken ? "text-orange-600 font-semibold" : "text-muted-foreground"}`}>{r.gorev_geciken}</td>
                </tr>
              ))}
            </tbody></table></div>
        </Card>
      </>)}
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-5 fade-in">
      <h1 className="font-head text-3xl font-semibold tracking-tight">Raporlar</h1>
      <Tabs defaultValue="alacak">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="alacak" data-testid="rep-tab-alacak">Alacak & Tahsilat</TabsTrigger>
          <TabsTrigger value="ay" data-testid="rep-tab-ay">Ay Sonu Özeti</TabsTrigger>
          <TabsTrigger value="beyanname" data-testid="rep-tab-beyanname">Beyanname Performansı</TabsTrigger>
          <TabsTrigger value="portfoy" data-testid="rep-tab-portfoy">Gelir & Portföy</TabsTrigger>
          <TabsTrigger value="personel" data-testid="rep-tab-personel">Personel İş Yükü</TabsTrigger>
          <TabsTrigger value="genel" data-testid="rep-tab-genel">Genel</TabsTrigger>
        </TabsList>
        <TabsContent value="alacak" className="mt-4"><Receivables /></TabsContent>
        <TabsContent value="ay" className="mt-4"><MonthSummary /></TabsContent>
        <TabsContent value="beyanname" className="mt-4"><DeclarationReport /></TabsContent>
        <TabsContent value="portfoy" className="mt-4"><PortfolioReport /></TabsContent>
        <TabsContent value="personel" className="mt-4"><WorkloadReport /></TabsContent>
        <TabsContent value="genel" className="mt-4"><General /></TabsContent>
      </Tabs>
    </div>
  );
}
